import * as React from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  GraduationCap,
  Loader2,
  Lock,
  Plus,
  Trash2,
  Users,
} from 'lucide-react'
import { ContentStatusBadge } from '../-components/ContentStatusBadge'
import { LessonEditorCard } from '../-components/LessonEditorCard'
import {
  AddChapterButton,
  ChapterHeadingRow,
  LessonChapterSelect,
} from '../-components/ChapterControls'
import { groupLessonsByChapter } from '../-components/course-chapters'
import { recurrenceLabel } from '../-components/recurrence-labels'
import { lessonProblem } from '../-components/lesson-readiness'
import { AttachmentsCard } from '../-components/AttachmentsCard'
import { AudienceRoster } from '../-components/AudienceRoster'
import { EditorActions } from '../-components/EditorActions'
import { IndividualAudienceList } from '../-components/IndividualAudienceList'
import { CourseMediaCard } from '../-components/CourseMediaCard'
import { CourseLifecycleAction } from '../-components/CourseLifecycleAction'
import { LessonPreviewButton } from '../-components/LessonPreviewDialog'
import { TermMultiSelect } from '../-components/TermMultiSelect'
import {
  audienceLabel,
  completionRuleLabel,
  lessonTypeLabel,
} from '../-components/learning-labels'
import type {
  LearningAudienceType,
  LearningLesson,
} from '@/types/learning.types'
import { PageTitle } from '@/components/PageTitle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/animate-ui/components/radix/alert-dialog'
import { useDebouncedValue } from '@/hooks/use-cases/useDebouncedValue'
import {
  useCreateLearningDraft,
  useLearningAudiencePreview,
  useLearningTerms,
  useLearningVersion,
  usePublishLearningVersion,
  useRemoveLearningLesson,
  useReorderLearningLessons,
  useSetLearningLessonChapter,
  useUpdateLearningVersion,
  useUpsertLearningLesson,
} from '@/hooks/repositories/useLearningRepository'
import { requirePermission } from '@/lib/permissions/route-guards'
import { LEARNING_PERMISSION_KEYS } from '@/lib/permissions/learning-permissions'
import { DEPARTMENT_LABELS, Department } from '@/types/department'

export const Route = createFileRoute('/_app/learning/admin/$versionId/')({
  beforeLoad: requirePermission(LEARNING_PERMISSION_KEYS.author),
  component: RouteComponent,
})

/**
 * Levels a course can target.
 *
 * CLIENT is excluded: the users table holds portal clients beside staff, and a
 * client is never staff being trained.
 */
const ASSIGNABLE_DEPARTMENTS = [
  Department.PLANNER,
  Department.ADVISOR,
  Department.SPECIALIST,
  Department.DIRECTOR,
  Department.ADMIN,
] as const

/** Radix Select cannot hold an empty string as a value, so "no term" needs one. */

/**
 * Course version editor (sc-1245).
 *
 * The page is built around one fact: a PUBLISHED version cannot be edited. When
 * viewing one, every control is read-only and the only action offered is
 * cloning it into a draft. Showing editable fields that fail on save would be a
 * worse way to communicate the same rule.
 */
function RouteComponent() {
  const { versionId } = Route.useParams()
  const navigate = useNavigate()
  const { data: version, isPending } = useLearningVersion(versionId)
  const { data: audience } = useLearningAudiencePreview(versionId)

  const updateVersion = useUpdateLearningVersion(versionId)
  const publishVersion = usePublishLearningVersion(versionId)
  const createDraft = useCreateLearningDraft()
  const upsertLesson = useUpsertLearningLesson(versionId)
  const removeLesson = useRemoveLearningLesson(versionId)
  const reorderLessons = useReorderLearningLessons(versionId)
  const setLessonChapter = useSetLearningLessonChapter(versionId)
  // Which chapter an "Add lesson" click was made from, so a new lesson lands
  // in it rather than at the bottom of the course to be dragged back.
  const [newLessonChapterId, setNewLessonChapterId] = React.useState<
    string | null
  >(null)

  // The roll-up the learner actually sees, mirrored here so an author is not
  // guessing what the course page will say.
  const lessonMinutes = (version?.lessons ?? []).reduce(
    (total, lesson) => total + (lesson.estimatedMinutes ?? 0),
    0,
  )

  const isReadOnly =
    version?.status === 'PUBLISHED' ||
    version?.status === 'ARCHIVED' ||
    Boolean(version?.course.archivedAt)

  const [editingLesson, setEditingLesson] = React.useState<
    LearningLesson | 'new' | null
  >(null)
  const [confirmRemove, setConfirmRemove] =
    React.useState<LearningLesson | null>(null)
  const [publishOpen, setPublishOpen] = React.useState(false)

  // ── Course fields, autosaved ────────────────────────────────────────────
  // Held locally and pushed on a debounce. Typing into a field that round-trips
  // per keystroke feels broken, and an explicit Save button on a draft nobody
  // else can see is ceremony for its own sake.
  const [title, setTitle] = React.useState('')
  const [summary, setSummary] = React.useState('')
  const [agenda, setAgenda] = React.useState('')
  const [minutes, setMinutes] = React.useState('')
  const [hydrated, setHydrated] = React.useState(false)

  const { data: categories = [] } = useLearningTerms('CATEGORY')
  const { data: technologies = [] } = useLearningTerms('TECHNOLOGY')

  // Written straight from the loaded version rather than mirrored into local
  // state: a Select has no typing to debounce, so it saves on change and the
  // query result is the single source of what is selected.
  const categoryIds = React.useMemo(
    () => (version?.categories ?? []).map((term) => term.id),
    [version?.categories],
  )
  // ANY onboarding category is enough — a course filed under both Onboarding
  // and Compliance is an onboarding course whichever one you were looking at.
  const isOnboardingCourse = categories.some(
    (term) => term.countsAsOnboarding && categoryIds.includes(term.id),
  )
  const technologyIds = React.useMemo(
    () => (version?.technologies ?? []).map((term) => term.id),
    [version?.technologies],
  )

  React.useEffect(() => {
    if (!version || hydrated) return
    setTitle(version.title)
    setSummary(version.summary ?? '')
    setAgenda(version.agendaMarkdown ?? '')
    setMinutes(version.estimatedMinutes ? String(version.estimatedMinutes) : '')
    setHydrated(true)
  }, [version, hydrated])

  const debouncedTitle = useDebouncedValue(title, 600)
  const debouncedSummary = useDebouncedValue(summary, 600)
  const debouncedAgenda = useDebouncedValue(agenda, 600)
  const debouncedMinutes = useDebouncedValue(minutes, 600)

  React.useEffect(() => {
    if (!version || !hydrated || isReadOnly) return

    const next = {
      title: debouncedTitle.trim(),
      summary: debouncedSummary.trim(),
      agendaMarkdown: debouncedAgenda.trim(),
      estimatedMinutes: debouncedMinutes ? Number(debouncedMinutes) : undefined,
    }
    const unchanged =
      next.title === version.title &&
      next.summary === (version.summary ?? '') &&
      next.agendaMarkdown === (version.agendaMarkdown ?? '') &&
      next.estimatedMinutes === (version.estimatedMinutes ?? undefined)

    if (unchanged || !next.title) return

    updateVersion.mutate({ versionId, payload: next })
    // updateVersion is a stable mutation object; including it would re-fire the
    // save on every render.
  }, [
    debouncedTitle,
    debouncedSummary,
    debouncedAgenda,
    debouncedMinutes,
    hydrated,
    isReadOnly,
    version,
    versionId,
  ])

  function moveLesson(index: number, direction: -1 | 1) {
    if (!version) return
    const ids = version.lessons.map((lesson) => lesson.id)
    const target = index + direction
    if (target < 0 || target >= ids.length) return
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    reorderLessons.mutate({ versionId, lessonIds: ids })
  }

  if (isPending || !version) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="py-4">
      <PageTitle title={`Learning — ${version.title}`} />

      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/learning/admin">
          <ArrowLeft className="mr-1 size-4" aria-hidden />
          Courses
        </Link>
      </Button>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
              {version.title}
            </h1>
            <ContentStatusBadge status={version.status} />
            <Badge variant="outline" className="text-[10px]">
              v{version.versionNumber}
            </Badge>
          </div>
          <p className="mt-1 font-mono text-[11px] text-muted-foreground">
            {version.course.slug}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* Discussion, revisions and sign-off live here as icons rather than
              cards: they are things you check occasionally, not fields you
              edit, and each icon carries its own state so you can see whether
              anything needs you without opening it. */}
          <EditorActions
            version={version}
            versionId={versionId}
            isReadOnly={isReadOnly}
          />
          {!version.course.archivedAt && (
            <CourseLifecycleAction
              courseId={version.course.id}
              title={version.title}
              hasPublishedVersion={Boolean(
                version.course.currentVersionId || version.publishedAt,
              )}
              onSuccess={() => void navigate({ to: '/learning/admin' })}
            />
          )}

          {/* Available at every status: the whole point is to read the course
              as an employee will BEFORE it is published to any. */}
          <Button asChild variant="outline">
            <Link
              to="/learning/admin/$versionId/preview"
              params={{ versionId }}
            >
              <Eye className="mr-1 size-4" aria-hidden />
              Preview
            </Link>
          </Button>
          {/* "People", not "Assign". The audience is chosen HERE and applied
              when the course publishes; that page is the live roster — who has
              it, how far they are, plus adding an individual, waiving someone
              and the repeat schedule. Calling it Assign sent people looking
              for a picker they had already used. */}
          {version.status === 'PUBLISHED' && (
            <Button asChild variant="outline">
              <Link
                to="/learning/admin/$versionId/assign"
                params={{ versionId }}
              >
                <Users className="mr-1 size-4" aria-hidden />
                People
              </Link>
            </Button>
          )}
          {isReadOnly ? (
            <Button
              onClick={() =>
                createDraft.mutate(version.id, {
                  // Land on the draft that was just made. Creating one and
                  // staying on the published version reads as nothing having
                  // happened — the toast is not enough when the page in front
                  // of you is still the read-only one.
                  onSuccess: (draft) =>
                    void navigate({
                      to: '/learning/admin/$versionId',
                      params: { versionId: draft.id },
                    }),
                })
              }
              disabled={
                createDraft.isPending || Boolean(version.course.archivedAt)
              }
            >
              {createDraft.isPending && (
                <Loader2 className="mr-1 size-4 animate-spin" aria-hidden />
              )}
              Edit as new draft
            </Button>
          ) : (
            <Button
              onClick={() => setPublishOpen(true)}
              disabled={version.lessons.length === 0}
              title={
                version.lessons.length === 0
                  ? 'A course needs at least one lesson to publish'
                  : undefined
              }
            >
              Publish
            </Button>
          )}
        </div>
      </div>

      {isReadOnly && (
        <Card className="mb-4 border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="flex items-start gap-3 py-4">
            <Lock
              className="mt-0.5 size-4 shrink-0 text-emerald-700 dark:text-emerald-400"
              aria-hidden
            />
            <div className="text-sm">
              <p className="font-medium">
                {version.status === 'PUBLISHED'
                  ? 'This version is live and cannot be edited'
                  : 'This version is archived and cannot be edited'}
              </p>
              <p className="mt-1 text-muted-foreground">
                {version.course.archivedAt ? (
                  'This course has been removed from active Learning. Its lessons, completion history, signatures and media are preserved.'
                ) : (
                  <>
                    People have been asked to complete this exact text. Editing
                    it would change what someone is halfway through, and would
                    make any acknowledgment already signed against it a claim
                    about wording that no longer exists. Create a draft to make
                    changes.
                  </>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Side by side, stretched to the same height, and NOT independently
          scrollable. Capping them at the viewport and scrolling inside meant
          two scrollbars fighting the page's own, and a card that started
          scrolling before the window was anywhere near full. The page scrolls;
          the cards are as tall as what is in them. */}
      <div className="grid items-stretch gap-4 lg:grid-cols-3">
        {/* ── Course details ───────────────────────────────────────────── */}
        <div className="flex flex-col gap-4 lg:col-span-2">
          {/* flex-1, and gap rather than space-y, so the card can be a flex
              column whose agenda field absorbs whatever height is left. That
              is what keeps the two columns level regardless of what is above
              it — cover image and intro video present, hidden, or one of
              each. A fixed row count cannot: it balances for exactly one of
              those combinations and is wrong for the rest. */}
          <Card className="flex flex-1 flex-col">
            <CardContent className="flex flex-1 flex-col gap-5 py-5">
              <h2 className="text-sm font-semibold">Course details</h2>

              {/* Length sits with the name because the two are read together
                  — "Logging a call, 12 min" is the whole of what somebody
                  scanning the catalogue sees. It was three fields further down,
                  which is why courses kept getting published without it. */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="version-title">Title</Label>
                  <Input
                    id="version-title"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="space-y-2 sm:w-36">
                  <Label htmlFor="version-minutes">Minutes</Label>
                  <Input
                    id="version-minutes"
                    type="number"
                    min={1}
                    value={minutes}
                    onChange={(event) => setMinutes(event.target.value)}
                    disabled={isReadOnly}
                    placeholder={lessonMinutes ? String(lessonMinutes) : ''}
                  />
                  {/* Lessons roll up: their estimates make a chapter's total
                      and the course's. This field is the fallback for a course
                      whose lessons carry none — so say what the lessons
                      already add up to rather than letting the two disagree
                      silently. */}
                  {lessonMinutes > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Lessons total {lessonMinutes} min
                    </p>
                  )}
                </div>
              </div>

              {/* Directly under the title, because the cover and the intro
                  are how the course introduces itself — they belong with the
                  name, not in a card further down. Behind a toggle so a course
                  that wants neither is not carrying two empty upload boxes. */}
              <CourseMediaCard
                version={version}
                isReadOnly={isReadOnly}
                onChange={(payload) =>
                  updateVersion.mutate({ versionId, payload })
                }
              />

              <div className="space-y-2">
                <Label htmlFor="version-summary">Summary</Label>
                <Textarea
                  id="version-summary"
                  value={summary}
                  onChange={(event) => setSummary(event.target.value)}
                  rows={4}
                  disabled={isReadOnly}
                />
              </div>

              {/* The one that grows. min-h keeps it usable when the sidebar
                  is short; flex-1 lets it take the rest when the sidebar is
                  long. */}
              <div className="flex flex-1 flex-col space-y-2">
                <Label htmlFor="version-agenda">
                  Agenda — what you&apos;ll learn
                </Label>
                <Textarea
                  id="version-agenda"
                  value={agenda}
                  onChange={(event) => setAgenda(event.target.value)}
                  placeholder="Markdown. Shown on the course intro page."
                  className="min-h-[10rem] flex-1 font-mono text-sm"
                  disabled={isReadOnly}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Sidebar ──────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-4">
          <Card>
            <CardContent className="space-y-4 py-5">
              <h2 className="text-sm font-semibold">Who gets this</h2>
              {/* The single most confusing thing about this screen: the choice
                  made here does nothing until the course publishes. */}
              <p className="-mt-2 text-xs text-muted-foreground">
                Applied when you publish.{' '}
                <Link
                  to="/learning/admin/$versionId/assign"
                  params={{ versionId }}
                  className="text-primary hover:underline"
                >
                  People
                </Link>{' '}
                shows who has it now.
              </p>

              {/* The repeat schedule lives on that page too, and nothing said
                  so — which is why "how do I make a course recurring" had no
                  answer. State it where the audience is decided, since it is
                  the same thought. */}
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
                <p className="font-medium">
                  {version.course.recurrence?.isActive
                    ? `Repeats ${recurrenceLabel(
                        version.course.recurrence.intervalMonths,
                      ).toLowerCase()}`
                    : 'Does not repeat'}
                </p>
                <p className="mt-0.5 text-muted-foreground">
                  {version.course.recurrence?.isActive
                    ? 'Everyone assigned retakes it on that schedule.'
                    : 'For a policy that needs re-acknowledging, set a repeat schedule.'}{' '}
                  <Link
                    to="/learning/admin/$versionId/assign"
                    params={{ versionId }}
                    className="text-primary hover:underline"
                  >
                    {version.course.recurrence?.isActive ? 'Change' : 'Set one'}
                  </Link>
                </p>
              </div>

              <div className="space-y-2">
                <Select
                  value={version.audienceType}
                  disabled={isReadOnly}
                  onValueChange={(value) =>
                    updateVersion.mutate({
                      versionId,
                      payload: {
                        audienceType: value as LearningAudienceType,
                        // Clear the level list when leaving that mode, so a
                        // course cannot keep a stale selection that no longer
                        // applies and would reappear on switching back.
                        audienceDepartments:
                          value === 'DEPARTMENT'
                            ? version.audienceDepartments
                            : [],
                      },
                    })
                  }
                >
                  <SelectTrigger aria-label="Audience">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* PERMISSION is deliberately absent — no learning.audience.*
                        keys are seeded, so offering it would be offering a
                        choice that always resolves to nobody. */}
                    {(['ALL_STAFF', 'DEPARTMENT', 'INDIVIDUAL'] as const).map(
                      (option) => (
                        <SelectItem key={option} value={option}>
                          {audienceLabel(option)}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>

                {version.audienceType === 'ALL_STAFF' && (
                  <p className="text-xs text-muted-foreground">
                    Everyone on staff. People who join later are assigned it
                    automatically, and show in Onboarding for their first 60
                    days.
                  </p>
                )}

                {version.audienceType === 'INDIVIDUAL' && (
                  <IndividualAudienceList
                    userIds={version.audienceUserIds}
                    disabled={isReadOnly}
                    onChange={(userIds) =>
                      updateVersion.mutate({
                        versionId,
                        payload: { audienceUserIds: userIds },
                      })
                    }
                  />
                )}
              </div>

              {version.audienceType === 'DEPARTMENT' && (
                <div className="space-y-2">
                  {ASSIGNABLE_DEPARTMENTS.map((department) => {
                    const checked =
                      version.audienceDepartments.includes(department)
                    return (
                      <label
                        key={department}
                        className="flex cursor-pointer items-center gap-2 text-sm"
                      >
                        <Checkbox
                          checked={checked}
                          disabled={isReadOnly}
                          onCheckedChange={() =>
                            updateVersion.mutate({
                              versionId,
                              payload: {
                                audienceDepartments: checked
                                  ? version.audienceDepartments.filter(
                                      (item) => item !== department,
                                    )
                                  : [
                                      ...version.audienceDepartments,
                                      department,
                                    ],
                              },
                            })
                          }
                        />
                        {DEPARTMENT_LABELS[department]}
                      </label>
                    )
                  })}
                  {/* The copy has to follow the SELECTION. "Anyone at these
                      levels" describes nothing when none are ticked, and read
                      straight into "0 people" underneath it. */}
                  {version.audienceDepartments.length === 0 ? (
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      Pick at least one level, or nobody is assigned.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Anyone at these levels, now and as people join — a new
                      starter is assigned once an admin sets their level.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Access levels, not org teams: someone at Advisor level,
                    rather than “the advisory team”.
                  </p>
                </div>
              )}

              {/* The rule above says who SHOULD get this; the roster below says
                  who actually has it, with the one action people reach for.
                  Only showing a count meant leaving for Assign to find out who
                  was on the hook, or to take anyone off. */}
              <AudienceRoster
                versionId={versionId}
                isPublished={version.status === 'PUBLISHED'}
              />

              {version.status !== 'PUBLISHED' && (
                <p className="text-xs text-muted-foreground">
                  {audience
                    ? `${audience.count} ${
                        audience.count === 1 ? 'person' : 'people'
                      } will be assigned when this publishes.`
                    : 'Counting…'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Categories and technologies are how a course is *found*, not
              what it says — so they sit with the other course-level settings
              in the rail rather than interrupting title → summary → agenda. */}
          <Card>
            <CardContent className="space-y-4 py-5">
              <h2 className="text-sm font-semibold">Filing</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Categories</Label>
                  <TermMultiSelect
                    terms={categories}
                    value={categoryIds}
                    disabled={isReadOnly}
                    label="category"
                    emptyHint="Not filed under anything yet"
                    onChange={(next) =>
                      updateVersion.mutate({
                        versionId,
                        payload: { categoryIds: next },
                      })
                    }
                  />
                  {/* The category IS the onboarding switch — there is no
                      separate per-course setting — so the consequence has to
                      be legible at the moment somebody picks one. */}
                  {isOnboardingCourse ? (
                    <p className="flex items-start gap-1.5 text-xs text-primary">
                      <GraduationCap
                        className="mt-0.5 size-3.5 shrink-0"
                        aria-hidden
                      />
                      <span>
                        An onboarding course. New starters stay in the
                        Onboarding panel until they finish it.
                      </span>
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      To make this an onboarding course, add a category marked{' '}
                      <GraduationCap
                        className="inline size-3 align-[-1px]"
                        aria-hidden
                      />{' '}
                      — or switch one on under{' '}
                      <Link
                        to="/learning/admin/properties"
                        className="underline underline-offset-2"
                      >
                        Properties
                      </Link>
                      .
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Technologies</Label>
                  <TermMultiSelect
                    terms={technologies}
                    value={technologyIds}
                    disabled={isReadOnly}
                    label="technology"
                    emptyHint="No tool in particular"
                    onChange={(next) =>
                      updateVersion.mutate({
                        versionId,
                        payload: { technologyIds: next },
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Add every tool the course actually covers — a course on
                    logging a call touches HubSpot and Chili Piper, and somebody
                    browsing either should find it.
                  </p>
                </div>
              </div>

              {/* Managed lists, so an author picks rather than types — a typo
                  used to reach the browse filter with nothing to correct it. */}
              <p className="-mt-2 text-xs text-muted-foreground">
                Missing one?{' '}
                <Link
                  to="/learning/admin/properties"
                  className="text-primary hover:underline"
                >
                  Manage properties
                </Link>
              </p>
            </CardContent>
          </Card>

          {/* Last in the sidebar and the one that grows, mirroring the agenda
              on the left. Whichever column is shorter, its growable card takes
              up the difference, so the two stay level either way. */}
          <AttachmentsCard
            className="flex-1"
            versionId={versionId}
            attachments={version.attachments}
            lessons={version.lessons}
            isReadOnly={isReadOnly}
          />
        </div>
      </div>

      {/* Lessons get the full width below: it is the longest thing on the
          page and the thing being edited, so it should not share a column. */}
      <div className="mt-4">
        {/* ── Lessons ────────────────────────────────────────────────── */}
        <Card>
          <CardContent className="space-y-4 py-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">
                Lessons{' '}
                <span className="font-normal text-muted-foreground">
                  ({version.lessons.length})
                </span>
              </h2>
              {!isReadOnly && editingLesson === null && (
                <div className="flex items-center gap-2">
                  <AddChapterButton versionId={versionId} />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setNewLessonChapterId(null)
                      setEditingLesson('new')
                    }}
                  >
                    <Plus className="mr-1 size-4" aria-hidden />
                    Add lesson
                  </Button>
                </div>
              )}
            </div>

            {version.lessons.length === 0 && editingLesson === null && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No lessons yet. A course needs at least one to publish.
              </p>
            )}

            <ol className="space-y-5">
              {groupLessonsByChapter(version.chapters, version.lessons).map(
                (group, groupIndex) => (
                  <li key={group.chapter?.id ?? 'ungrouped'}>
                    {group.chapter ? (
                      <ChapterHeadingRow
                        versionId={versionId}
                        chapter={
                          version.chapters.find(
                            (row) => row.id === group.chapter?.id,
                          ) ?? version.chapters[groupIndex]
                        }
                        lessons={group.lessons}
                        index={groupIndex}
                        total={version.chapters.length}
                        chapterIds={version.chapters.map((row) => row.id)}
                        isReadOnly={isReadOnly}
                      />
                    ) : (
                      version.chapters.length > 0 && (
                        <p className="border-b pb-1.5 text-xs text-muted-foreground">
                          Not in a chapter
                        </p>
                      )
                    )}
                    <ol className="mt-2 space-y-2">
                      {group.lessons.map((lesson) => {
                        // The number a learner sees is the course-wide one, not the
                        // position within the chapter — "lesson 7 of 12" is what the
                        // player and the progress bar both count in.
                        const index = version.lessons.indexOf(lesson)
                        return editingLesson !== 'new' &&
                          editingLesson?.id === lesson.id ? (
                          <li key={lesson.id}>
                            <LessonEditorCard
                              courseVersionId={versionId}
                              attachments={version.attachments}
                              lesson={lesson}
                              position={lesson.position}
                              isSaving={upsertLesson.isPending}
                              onCancel={() => setEditingLesson(null)}
                              onSave={(payload, options) =>
                                upsertLesson.mutate(payload, {
                                  // keepOpen is the attach-a-video autosave: closing
                                  // the editor mid-edit is the bug that made the last
                                  // autosave attempt unusable.
                                  onSuccess: () => {
                                    if (!options?.keepOpen)
                                      setEditingLesson(null)
                                  },
                                })
                              }
                            />
                          </li>
                        ) : (
                          <li
                            key={lesson.id}
                            className="flex items-start gap-3 rounded-md border p-3"
                          >
                            <span className="mt-0.5 w-5 shrink-0 text-center text-xs text-muted-foreground tabular-nums">
                              {index + 1}
                            </span>

                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium">
                                {lesson.title}
                              </p>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                <Badge
                                  variant="outline"
                                  className="text-[10px]"
                                >
                                  {lessonTypeLabel(lesson.type)}
                                </Badge>
                                {/* Saved but not finishable (sc-1335). Lessons can
                              be saved half-written now, so the list has to say
                              which ones will block publishing — otherwise you
                              only find out when publish is refused. */}
                                {lessonProblem(lesson) && (
                                  <Badge
                                    variant="outline"
                                    className="border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-700 dark:text-amber-400"
                                  >
                                    Unfinished — {lessonProblem(lesson)}
                                  </Badge>
                                )}
                                <span>
                                  {completionRuleLabel(lesson.completionRule)}
                                </span>
                                {lesson.estimatedMinutes != null && (
                                  <span className="inline-flex items-center gap-1">
                                    <Clock className="size-3" aria-hidden />
                                    {lesson.estimatedMinutes} min
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Preview is outside the read-only guard: looking at a
                          lesson changes nothing, and it is just as useful on a
                          published version as on a draft. */}
                            <div className="flex shrink-0 items-center gap-1">
                              <LessonPreviewButton lesson={lesson} />
                            </div>

                            {/* Move a lesson between chapters without dragging.
                          Hidden entirely when the course has no chapters —
                          a picker with one option is just noise. */}
                            {!isReadOnly && version.chapters.length > 0 && (
                              <LessonChapterSelect
                                chapters={version.chapters}
                                value={lesson.chapterId}
                                disabled={setLessonChapter.isPending}
                                onChange={(chapterId) =>
                                  setLessonChapter.mutate({
                                    lessonId: lesson.id,
                                    chapterId,
                                  })
                                }
                              />
                            )}

                            {!isReadOnly && (
                              <div className="flex shrink-0 items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => moveLesson(index, -1)}
                                  disabled={
                                    index === 0 || reorderLessons.isPending
                                  }
                                  aria-label={`Move ${lesson.title} up`}
                                >
                                  <ChevronUp className="size-4" aria-hidden />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => moveLesson(index, 1)}
                                  disabled={
                                    index === version.lessons.length - 1 ||
                                    reorderLessons.isPending
                                  }
                                  aria-label={`Move ${lesson.title} down`}
                                >
                                  <ChevronDown className="size-4" aria-hidden />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingLesson(lesson)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setConfirmRemove(lesson)}
                                  aria-label={`Remove ${lesson.title}`}
                                >
                                  <Trash2
                                    className="size-4 text-destructive"
                                    aria-hidden
                                  />
                                </Button>
                              </div>
                            )}
                          </li>
                        )
                      })}
                    </ol>
                    {!isReadOnly && group.chapter && editingLesson === null && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-1 h-7 text-xs text-muted-foreground"
                        onClick={() => {
                          setNewLessonChapterId(group.chapter?.id ?? null)
                          setEditingLesson('new')
                        }}
                      >
                        <Plus className="mr-1 size-3.5" aria-hidden />
                        Add lesson to {group.chapter.title}
                      </Button>
                    )}
                  </li>
                ),
              )}
            </ol>

            {editingLesson === 'new' && (
              <>
                {newLessonChapterId && (
                  <p className="mb-2 text-xs text-muted-foreground">
                    Adding to{' '}
                    <span className="font-medium text-foreground">
                      {
                        version.chapters.find(
                          (row) => row.id === newLessonChapterId,
                        )?.title
                      }
                    </span>
                  </p>
                )}
                <LessonEditorCard
                  courseVersionId={versionId}
                  attachments={version.attachments}
                  position={version.lessons.length}
                  isSaving={upsertLesson.isPending}
                  onCancel={() => {
                    setEditingLesson(null)
                    setNewLessonChapterId(null)
                  }}
                  onSave={(payload) =>
                    upsertLesson.mutate(
                      { ...payload, chapterId: newLessonChapterId },
                      {
                        onSuccess: () => {
                          setEditingLesson(null)
                          setNewLessonChapterId(null)
                        },
                      },
                    )
                  }
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Publish ────────────────────────────────────────────────────── */}
      <AlertDialog open={publishOpen} onOpenChange={setPublishOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Publish v{version.versionNumber}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  This becomes the version learners see. How you publish it
                  decides who gets asked to take it again.
                </p>
                <ul className="space-y-2">
                  <li>
                    <strong className="text-foreground">Major</strong> —
                    re-assigns to all {audience ? audience.count : 'affected'}{' '}
                    people in the audience. For anything that changes what
                    someone is agreeing to.
                  </li>
                  <li>
                    <strong className="text-foreground">Minor</strong> — nobody
                    is re-assigned. For typos and formatting.
                  </li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() =>
                publishVersion.mutate(
                  { versionId, revisionKind: 'MINOR' },
                  { onSuccess: () => setPublishOpen(false) },
                )
              }
              disabled={publishVersion.isPending}
            >
              Publish as minor
            </Button>
            <AlertDialogAction
              onClick={() =>
                publishVersion.mutate(
                  { versionId, revisionKind: 'MAJOR' },
                  { onSuccess: () => setPublishOpen(false) },
                )
              }
              disabled={publishVersion.isPending}
            >
              Publish as major
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Remove lesson ──────────────────────────────────────────────── */}
      <AlertDialog
        open={confirmRemove !== null}
        onOpenChange={(open) => !open && setConfirmRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove “{confirmRemove?.title}”?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This only affects this draft. Published versions keep their
              lessons exactly as they were.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmRemove) removeLesson.mutate(confirmRemove.id)
                setConfirmRemove(null)
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
